import supabaseSystemConfigService from './supabaseSystemConfigService'

class ConfigCacheService {
    constructor() {
        this.cache = new Map()
        this.imageCache = new Map()
        this.sessionStorage = window.sessionStorage
        this.localStorage = window.localStorage
        this.cachePrefix = 'mahiru_config_'
        this.imageCachePrefix = 'mahiru_image_'
        this.maxImageCacheSize = 50 // 最大缓存50张图片
        this.cacheExpiry = 1000 * 60 * 30 // 30分钟过期
    }

    /**
     * 预加载网站配置
     */
    async preloadSiteConfig() {
        try {
            console.log('🚀 开始预加载网站配置...')

            // 先尝试从缓存获取
            const cachedConfig = this.getFromCache('site.config')
            if (cachedConfig) {
                console.log('✅ 从缓存获取网站配置:', cachedConfig)
                return cachedConfig
            }

            // 从数据库获取最新配置
            const config = await supabaseSystemConfigService.getSiteConfig()
            console.log('📡 从数据库获取网站配置:', config)

            // 缓存配置
            this.setToCache('site.config', config)

            // 预加载背景资源
            if (config.backgroundUrl && config.backgroundType) {
                await this.preloadBackgroundResource(config.backgroundUrl, config.backgroundType)
            }

            // 预加载logo
            if (config.logo) {
                await this.preloadImage(config.logo, 'site-logo')
            }

            return config
        } catch (error) {
            console.error('❌ 预加载网站配置失败:', error)
            // 返回默认配置
            return this.getDefaultSiteConfig()
        }
    }

    /**
     * 预加载背景资源
     */
    async preloadBackgroundResource(backgroundUrl, backgroundType) {
        try {
            console.log(`🎨 预加载背景资源: ${backgroundType} - ${backgroundUrl}`)

            if (backgroundType === 'image') {
                await this.preloadImage(backgroundUrl, 'site-background')
            } else if (backgroundType === 'video') {
                await this.preloadVideo(backgroundUrl)
            }
        } catch (error) {
            console.warn('⚠️ 背景资源预加载失败:', error)
        }
    }

    /**
     * 预加载图片
     */
    async preloadImage(url, cacheKey = null) {
        return new Promise((resolve, reject) => {
            // 检查是否已缓存
            const finalCacheKey = cacheKey || this.generateImageCacheKey(url)
            if (this.imageCache.has(finalCacheKey)) {
                console.log('✅ 图片已在缓存中:', url)
                resolve(this.imageCache.get(finalCacheKey))
                return
            }

            const img = new Image()
            img.onload = () => {
                console.log('✅ 图片预加载成功:', url)

                // 缓存图片信息
                const imageInfo = {
                    url,
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                    loadTime: Date.now(),
                    element: img
                }

                this.cacheImage(finalCacheKey, imageInfo)
                resolve(imageInfo)
            }

            img.onerror = (error) => {
                console.error('❌ 图片预加载失败:', url, error)
                reject(error)
            }

            img.src = url
        })
    }

    /**
     * 预加载视频
     */
    async preloadVideo(url) {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video')
            video.preload = 'metadata'
            video.muted = true

            video.onloadedmetadata = () => {
                console.log('✅ 视频元数据加载成功:', url)
                resolve({
                    url,
                    duration: video.duration,
                    width: video.videoWidth,
                    height: video.videoHeight,
                    element: video
                })
            }

            video.onerror = (error) => {
                console.error('❌ 视频预加载失败:', url, error)
                reject(error)
            }

            video.src = url
        })
    }

    /**
     * 缓存图片信息
     */
    cacheImage(key, imageInfo) {
        // 如果缓存已满，删除最旧的条目
        if (this.imageCache.size >= this.maxImageCacheSize) {
            const oldestKey = this.imageCache.keys().next().value
            this.imageCache.delete(oldestKey)
            console.log('🗑️ 删除最旧的图片缓存:', oldestKey)
        }

        this.imageCache.set(key, imageInfo)
        console.log('💾 图片已缓存:', key, imageInfo.url)
    }

    /**
     * 获取缓存的图片
     */
    getCachedImage(key) {
        return this.imageCache.get(key)
    }

    /**
     * 生成图片缓存键
     */
    generateImageCacheKey(url) {
        return `img_${this.hashString(url)}`
    }

    /**
     * 设置配置到缓存
     */
    setToCache(key, value, useSession = true) {
        const cacheData = {
            value,
            timestamp: Date.now(),
            expiry: Date.now() + this.cacheExpiry
        }

        // 内存缓存
        this.cache.set(key, cacheData)

        // 持久化缓存
        const storage = useSession ? this.sessionStorage : this.localStorage
        try {
            storage.setItem(this.cachePrefix + key, JSON.stringify(cacheData))
        } catch (error) {
            console.warn('缓存存储失败:', error)
        }
    }

    /**
     * 从缓存获取配置
     */
    getFromCache(key, useSession = true) {
        // 先从内存缓存获取
        const memoryCache = this.cache.get(key)
        if (memoryCache && memoryCache.expiry > Date.now()) {
            return memoryCache.value
        }

        // 从持久化缓存获取
        const storage = useSession ? this.sessionStorage : this.localStorage
        try {
            const cached = storage.getItem(this.cachePrefix + key)
            if (cached) {
                const cacheData = JSON.parse(cached)
                if (cacheData.expiry > Date.now()) {
                    // 更新内存缓存
                    this.cache.set(key, cacheData)
                    return cacheData.value
                } else {
                    // 过期了，删除
                    storage.removeItem(this.cachePrefix + key)
                }
            }
        } catch (error) {
            console.warn('读取缓存失败:', error)
        }

        return null
    }

    /**
     * 清除配置缓存
     */
    clearCache(key = null) {
        if (key) {
            this.cache.delete(key)
            this.sessionStorage.removeItem(this.cachePrefix + key)
            this.localStorage.removeItem(this.cachePrefix + key)
        } else {
            this.cache.clear()
            // 清除所有相关的存储项
            this.clearStorageByPrefix(this.sessionStorage, this.cachePrefix)
            this.clearStorageByPrefix(this.localStorage, this.cachePrefix)
        }
    }

    /**
     * 清除图片缓存
     */
    clearImageCache() {
        this.imageCache.clear()
        console.log('🗑️ 图片缓存已清除')
    }

    /**
     * 根据前缀清除存储
     */
    clearStorageByPrefix(storage, prefix) {
        const keysToRemove = []
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i)
            if (key && key.startsWith(prefix)) {
                keysToRemove.push(key)
            }
        }
        keysToRemove.forEach(key => storage.removeItem(key))
    }

    /**
     * 获取默认网站配置
     */
    getDefaultSiteConfig() {
        return {
            title: 'MAHIRU的博客',
            description: '记录生活中的美好瞬间',
            logo: '/images/mahiru-logo.svg',
            backgroundType: 'image',
            backgroundUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop'
        }
    }

    /**
     * 简单的字符串哈希函数
     */
    hashString(str) {
        let hash = 0
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash = hash & hash // 转换为32位整数
        }
        return Math.abs(hash).toString(36)
    }

    /**
     * 应用网站配置到页面
     */
    applySiteConfig(config) {
        try {
            // 应用标题
            if (config.title) {
                document.title = config.title
            }

            // 应用背景
            if (config.backgroundUrl && config.backgroundType) {
                this.applyBackground(config.backgroundUrl, config.backgroundType)
            }

            console.log('✅ 网站配置已应用:', config)
        } catch (error) {
            console.error('❌ 应用网站配置失败:', error)
        }
    }

    /**
     * 应用背景
     */
    applyBackground(backgroundUrl, backgroundType) {
        const body = document.body

        if (backgroundType === 'image') {
            // 移除可能存在的视频背景
            this.removeVideoBackground()

            // 应用图片背景
            body.style.backgroundImage = `url(${backgroundUrl})`
            body.style.backgroundSize = 'cover'
            body.style.backgroundPosition = 'center'
            body.style.backgroundRepeat = 'no-repeat'
            body.style.backgroundAttachment = 'fixed'

        } else if (backgroundType === 'video') {
            // 移除图片背景
            body.style.backgroundImage = ''

            // 应用视频背景
            this.applyVideoBackground(backgroundUrl)
        }
    }

    /**
     * 应用视频背景
     */
    applyVideoBackground(videoUrl) {
        // 移除现有的视频背景
        this.removeVideoBackground()

        const video = document.createElement('video')
        video.id = 'site-background-video'
        video.autoplay = true
        video.loop = true
        video.muted = true
        video.playsInline = true
        video.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: -1;
            pointer-events: none;
        `

        video.src = videoUrl
        document.body.appendChild(video)

        video.play().catch(error => {
            console.warn('视频自动播放失败:', error)
        })
    }

    /**
     * 移除视频背景
     */
    removeVideoBackground() {
        const existingVideo = document.getElementById('site-background-video')
        if (existingVideo) {
            existingVideo.remove()
        }
    }

    /**
     * 获取缓存统计信息
     */
    getCacheStats() {
        return {
            configCacheSize: this.cache.size,
            imageCacheSize: this.imageCache.size,
            maxImageCacheSize: this.maxImageCacheSize
        }
    }
}

export default new ConfigCacheService() 